import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Orbit, Leaf, Heart, ArrowRight } from "lucide-react";

const HOLISTIC = "https://images.unsplash.com/photo-1506126613408-eca07ce68773";

export default function WhatIs() {
  return (
    <div data-testid="page-what-is">
      <section className="section">
        <div className="container-page">
          <div className="max-w-4xl">
            <div className="eyebrow mb-6">The Method</div>
            <h1 className="text-5xl md:text-7xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What is Astrohomeopathy<sup className="text-[#D4AF37] text-3xl">®</sup>?
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-[#475569] leading-relaxed font-serif" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              A clinical synthesis that uses the natal chart as a diagnostic map, and homeopathic remedies as the therapeutic keys — developed and refined over 25 years by Dr. Manish Misra.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-page grid md:grid-cols-2 gap-12 items-center">
          <motion.img initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} src={HOLISTIC} alt="Holistic healing" className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl" />
          <div>
            <h2 className="text-4xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Two ancient sciences, one modern practice.</h2>
            <p className="text-[#475569] leading-relaxed mb-4">Classical homeopathy, discovered by Samuel Hahnemann in 1796, treats the person — not the disease — using highly diluted natural substances that match the patient's constitutional picture.</p>
            <p className="text-[#475569] leading-relaxed mb-4">Vedic astrology (Jyotisha), the "science of light," maps how planetary energies at the moment of birth shape the individual's physiology, psychology, and karmic tendencies.</p>
            <p className="text-[#475569] leading-relaxed">Astrohomeopathy<sup className="text-[#D4AF37]">®</sup> is the disciplined union of the two: the natal chart tells us <em>which</em> planetary force is afflicted; the Materia Medica tells us <em>which</em> remedy resonates with that force.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-[#F0F9FF]/60">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-4">Process</div>
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The consultation, in four movements.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Case Taking", d: "A deep exploration of physical symptoms, mental state, family history, and lifestyle." },
              { n: "02", t: "Chart Analysis", d: "Your natal chart is drawn and studied for planetary afflictions, dashas, and yogas." },
              { n: "03", t: "Simillimum Selection", d: "The remedy is chosen from the planetary family that governs your afflicted graha." },
              { n: "04", t: "Follow-Through", d: "Response is tracked; potencies adjusted; miasmatic layers addressed sequentially." },
            ].map((s) => (
              <div key={s.n} className="glass-card p-8">
                <div className="text-5xl font-serif text-[#D4AF37]/40" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.n}</div>
                <h3 className="text-2xl mt-4 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.t}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IT TREATS */}
      <section className="section">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-4">Scope</div>
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Conditions we have addressed.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { i: Heart, t: "Chronic ailments", items: ["Autoimmune disorders", "Migraine & chronic headaches", "Skin conditions (eczema, psoriasis)", "Arthritis & joint pain"] },
              { i: Orbit, t: "Mind & mood", items: ["Anxiety & panic", "Depression", "Insomnia", "PTSD & grief"] },
              { i: Leaf, t: "Hormonal & women's health", items: ["PCOS & endometriosis", "Menstrual irregularities", "Menopause support", "Fertility"] },
            ].map((g, i) => (
              <div key={i} className="rounded-3xl border border-slate-100 p-8 bg-white">
                <g.i size={22} className="text-[#0EA5E9] mb-4" strokeWidth={1.5} />
                <h3 className="text-2xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{g.t}</h3>
                <ul className="space-y-2 text-sm text-[#475569]">
                  {g.items.map((it) => (
                    <li key={it} className="flex gap-2"><span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0F172A] text-white">
        <div className="container-page text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>See it in practice.</h2>
          <p className="text-white/70 mb-8 text-lg">Explore how the nine grahas map to homeopathic remedy families.</p>
          <Link to="/planets" data-testid="whatis-planets-cta" className="btn-gold">Open the Planet Explorer <ArrowRight size={16} className="ml-2" /></Link>
        </div>
      </section>
    </div>
  );
}
